import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { GitCompareArrows } from "lucide-react";

// A focused, on-brand progress overlay shown WHILE the "what changed"
// comparison runs (both the full auto-run and a single-row re-compare).
// The review page streams notes in live, which on its own can read as
// "already done" — this modal makes the in-flight work unmistakable, then
// gets out of the way the moment the run settles.
export function CompareOverlay({
  fileName,
  message,
  total,
  done,
  rowScoped,
  onCancel,
}: {
  fileName: string;
  message: string;
  total: number;
  done: number;
  rowScoped: boolean;
  onCancel: () => void;
}) {
  // Lock body scroll while the overlay owns the screen.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const shown = Math.min(done, total);
  const determinate = total > 0;
  const pct = determinate ? Math.round((shown / total) * 100) : 0;

  // The count arrives on its own typed event (the bar label shows it); the
  // message is pure prose — current phase + sheet id.
  const status = message;

  return createPortal(
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Comparing plans"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6 py-8 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
        className="relative w-full max-w-[440px] overflow-hidden rounded-2xl bg-surface p-8 text-center shadow-lift"
      >
        {/* Soft pastel halo behind the emblem. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-primary-soft/60 blur-3xl"
        />

        <Emblem />

        <h2 className="relative mt-6 font-display text-[1.35rem] font-medium tracking-tight text-ink">
          {rowScoped ? "Re-comparing this sheet" : "Finding what changed"}
        </h2>
        <p className="relative mx-auto mt-2 max-w-[20rem] text-[0.9rem] leading-relaxed text-ink-soft">
          Comparing against{" "}
          <span className="font-medium text-ink">{fileName}</span>
        </p>

        {/* Live streaming status from the server. */}
        <div className="relative mt-5 flex min-h-[1.25rem] items-center justify-center gap-2 text-[0.85rem] text-ink-muted">
          <motion.span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <span className="truncate">{status}</span>
        </div>

        {/* Progress: determinate once we know the sheet count, indeterminate
            during the upload/index phase before any sheet resolves. */}
        <div className="relative mt-5">
          {determinate ? (
            <>
              <div className="h-2 overflow-hidden rounded-full bg-canvas-2">
                <motion.div
                  className="h-full rounded-full bg-primary"
                  initial={false}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                />
              </div>
              <div className="mt-2 text-[0.78rem] font-medium text-ink-muted">
                {shown} of {total} sheet{total === 1 ? "" : "s"} described
              </div>
            </>
          ) : (
            <IndeterminateBar />
          )}
        </div>

        <button
          type="button"
          onClick={onCancel}
          className="relative mt-6 text-[0.82rem] font-medium text-ink-muted underline decoration-ink-faint/40 underline-offset-2 transition-colors hover:text-ink"
        >
          Cancel comparison
        </button>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

// Center icon inside a spinning conic ring — the signature "working" moment.
function Emblem() {
  return (
    <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, hsl(var(--primary) / 0) 0deg, hsl(var(--primary) / 0) 80deg, hsl(var(--primary) / 0.5) 300deg, hsl(var(--primary)) 360deg)",
          WebkitMask:
            "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 4px), #000 calc(100% - 4px))",
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
      />
      <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary-ink">
        <motion.span
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <GitCompareArrows className="h-6 w-6" aria-hidden />
        </motion.span>
      </div>
    </div>
  );
}

function IndeterminateBar() {
  return (
    <div className="h-2 overflow-hidden rounded-full bg-canvas-2">
      <motion.div
        animate={{ x: ["-100%", "350%"] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        className="h-full w-1/3 rounded-full bg-primary"
      />
    </div>
  );
}
